import { SagaIterator } from 'redux-saga';
import { all, put, select, takeEvery } from 'redux-saga/effects';
import ddt from '@wix/da-ddt';
import { receiveHubMessage } from './actions';
import { addNotificationToQueue } from '../../Notifications/redux/actionCreators';
import { NotificationType } from '../../Notifications/redux/types';
import { HubChannel, LiveHubNotificationPayload } from './types';
import { dreamupGenerationSucceeded, dreamupGenerationFailed } from '../dreamup/actions';
import { batchDeviationUpdateComplete } from '../deviations/actionCreators';
import { getIsMobile } from '../../publicSession/selectors';
import {
  getPushPocEnabled,
  getPushSubscriptionState,
  getPushSubscriptions,
} from '../pwa/selectors';
import { isPushCategoryEnabled, supportsDesktopWebPush } from '../pwa/helpers';
import { PushSubscriptionState, PushSubscriptions } from '../pwa/types';

function shouldSuppressLiveNotification(
  notificationData: LiveHubNotificationPayload,
  isMobile: boolean,
  pushPocEnabled: boolean,
  pushState: PushSubscriptionState,
  pushSubscriptions: PushSubscriptions
) {
  if (!pushPocEnabled || !supportsDesktopWebPush(isMobile)) {
    return false;
  }

  if (pushState !== PushSubscriptionState.SUBSCRIBED) {
    return false;
  }

  return isPushCategoryEnabled(pushSubscriptions, notificationData.push_category);
}

export function* handleHubMessage({ payload }) {
  const { channel, data } = payload;

  switch (channel) {
    case HubChannel.NOTIFICATIONS: {
      const notificationData = data.payload as LiveHubNotificationPayload;
      const isMobile: boolean = yield select(getIsMobile);
      const pushPocEnabled: boolean = yield select(getPushPocEnabled);
      const pushState: PushSubscriptionState = yield select(getPushSubscriptionState);
      const pushSubscriptions: PushSubscriptions = yield select(getPushSubscriptions);

      if (
        shouldSuppressLiveNotification(
          notificationData,
          isMobile,
          pushPocEnabled,
          pushState,
          pushSubscriptions
        )
      ) {
        ddt.log('hub', 'live notification suppressed under push_poc', {
          pushCategory: notificationData.push_category,
          notificationData,
        });
        break;
      }

      yield put(addNotificationToQueue('live', NotificationType.Live, notificationData));
      ddt.log('hub', 'live notification enqueued', notificationData);
      break;
    }
    case HubChannel.DREAMSOFART: {
      const dreamupData = data.payload;
      if (dreamupData.status === 'complete') {
        yield put(dreamupGenerationSucceeded(dreamupData.generation_id, dreamupData.deviationids));
      } else if (dreamupData.status === 'error') {
        yield put(
          dreamupGenerationFailed(
            dreamupData.generation_id,
            dreamupData.free_prompts_remaining,
            dreamupData.error
          )
        );
      } else {
        yield put(
          dreamupGenerationFailed(
            dreamupData.generation_id,
            dreamupData.free_prompts_remaining,
            undefined
          )
        );
      }
      ddt.log('hub', 'dreamup art generation finished', dreamupData);
      break;
    }
    case HubChannel.DREAMSOFART_INPAINT: {
      const inpaintData = data.payload;
      const muro = globalThis.__MURO;
      if (!muro) {
        break;
      }
      if (inpaintData.status === 'complete') {
        ddt.log('hub', 'Inpaint generation succeeded', inpaintData);
        muro.reduxBridge({
          type: 'INPAINT_SUCCESS',
          payload: { generationId: inpaintData.generation_id },
        });
      } else if (inpaintData.status === 'error') {
        ddt.error('hub', 'Inpaint generation failed', inpaintData);
        muro.reduxBridge({
          type: 'INPAINT_ERROR',
          payload: {
            generationId: inpaintData.generation_id,
            errorCode: inpaintData.errorCode,
          },
        });
      } else {
        ddt.error('hub', 'Inpaint generation - coin landed on its side?', inpaintData);
        muro.reduxBridge({
          type: 'INPAINT_ERROR',
          payload: {
            generationId: inpaintData.generation_id,
            errorCode: undefined,
          },
        });
      }
      break;
    }
    case HubChannel.BATCH_COMPLETE: {
      const batchData = data.payload;
      yield put(
        batchDeviationUpdateComplete({
          batchId: batchData.batchid,
          name: batchData.name,
          processedCount: parseInt(batchData.total_processed || '0'),
          failedCount: parseInt(batchData.errors || '0'),
        })
      );
      ddt.log('hub', 'Batch completed', batchData);
      break;
    }
    default:
      break;
  }
}

export default function* saga(): SagaIterator {
  yield all([takeEvery(receiveHubMessage, handleHubMessage)]);
}
